import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, BookOpen, Paintbrush } from "lucide-react";
import { guidesData } from "@/data/guidesData";
import Footer from "@/components/Footer";

export default function GuidesPage() {
  return (
    <main className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="container mx-auto max-w-6xl px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#b91c1c] rounded-lg flex items-center justify-center">
              <Paintbrush className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">fasadmalning.se</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-300 hover:text-white transition-colors">
              Hem
            </Link>
            <Link to="/guider" className="text-white font-medium">
              Guider
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <BookOpen className="w-4 h-4 text-[#b91c1c]" />
              <span className="text-sm font-medium text-slate-700">Kunskap & tips</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
              Guider om fasadmålning
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Lär dig allt om fasadmålning – från förarbete till färgval. 
              Våra experter delar med sig av sina bästa tips.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24" data-testid="guides-grid">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {guidesData.map((guide) => (
              <Link
                key={guide.id}
                to={`/guider/${guide.slug}`}
                className="group"
                data-testid={`guide-card-${guide.id}`}
              >
                <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {/* Image */}
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={guide.image}
                      alt={guide.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#b91c1c] bg-[#b91c1c]/10 px-3 py-1 rounded-full">
                        {guide.category}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {guide.readTime}
                      </span>
                    </div>
                    
                    <h2 className="text-xl md:text-2xl font-semibold text-slate-900 group-hover:text-[#b91c1c] transition-colors leading-tight">
                      {guide.title}
                    </h2>
                    
                    <p className="text-slate-600 leading-relaxed">
                      {guide.excerpt}
                    </p>
                    
                    <div className="flex items-center gap-2 text-[#b91c1c] font-medium pt-2">
                      Läs mer
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="container mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Redo att måla om din fasad?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Kontakta oss för en kostnadsfri besiktning och offert.
          </p>
          <Link to="/#contact-form">
            <Button 
              className="bg-[#b91c1c] hover:bg-[#991b1b] text-white h-14 px-8 rounded-full text-lg font-semibold shadow-lg"
              data-testid="guides-cta-button"
            >
              Få kostnadsfri offert
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
